(() => {
  'use strict';
  class StageMenu extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      const button = this.querySelector('.cp-stage-menu-toggle');
      const nav = this.querySelector('.cp-stage-nav');
      if (!button || !nav) return;
      const setOpen = (open, focus = false) => {
        button.setAttribute('aria-expanded', String(open));
        button.textContent = open ? this.dataset.close : this.dataset.menu;
        nav.classList.toggle('is-open', open);
        if (focus) button.focus();
      };
      button.addEventListener('click', () => setOpen(button.getAttribute('aria-expanded') !== 'true'), options);
      nav.addEventListener('click', event => { if (event.target.closest('a')) setOpen(false); }, options);
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && button.getAttribute('aria-expanded') === 'true') setOpen(false, true);
      }, options);
      document.addEventListener('click', event => { if (!this.contains(event.target)) setOpen(false); }, options);
    }
    disconnectedCallback() { this.abort?.abort(); }
  }

  class StageRotation extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.products = JSON.parse(this.querySelector('[data-rotation-products]')?.textContent || '[]');
      this.visual = this.querySelector('.hero-product');
      if (!this.visual || this.products.length < 2) return;
      this.index = 0;
      this.busy = false;
      this.inView = true;
      this.hovered = false;
      this.focused = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches || window.Shopify?.designMode === true;
      this.controls = this.querySelector('.rotation');
      this.pauseButton = this.querySelector('.pause');
      this.controls.hidden = false;
      this.querySelector('.prev').addEventListener('click', () => this.show(this.index - 1, true), options);
      this.querySelector('.next').addEventListener('click', () => this.show(this.index + 1, true), options);
      this.pauseButton.addEventListener('click', () => { this.paused = !this.paused; this.sync(); this.schedule(); }, options);
      this.motion.addEventListener('change', event => { if (event.matches) { this.paused = true; this.sync(); this.schedule(); } }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, options);
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.schedule(); } }, options);
      this.observer = new IntersectionObserver(entries => { this.inView = entries[0].isIntersecting; this.schedule(); }, { threshold: 0.1 });
      this.observer.observe(this);
      this.sync();
      this.schedule();
    }
    sync() {
      this.pauseButton.textContent = this.paused ? this.dataset.play : this.dataset.pause;
      this.pauseButton.setAttribute('aria-pressed', String(this.paused));
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.paused || document.hidden || !this.inView || this.hovered || this.focused || !this.isConnected) return;
      this.timer = setTimeout(() => this.show(this.index + 1, false), Number(this.dataset.interval) || 9000);
    }
    async show(next, manual) {
      if (this.busy || !this.isConnected) return;
      clearTimeout(this.timer);
      this.busy = true;
      const index = (next + this.products.length) % this.products.length;
      const product = this.products[index];
      const preload = new Image();
      preload.sizes = this.visual.sizes;
      preload.srcset = product.srcset;
      preload.src = product.image;
      try { await preload.decode(); } catch { this.busy = false; this.schedule(); return; }
      if (!this.isConnected) return;
      this.visual.style.opacity = '0';
      this.transitionTimer = setTimeout(() => {
        if (!this.isConnected) return;
        this.index = index;
        this.visual.srcset = product.srcset;
        this.visual.src = product.image;
        this.visual.alt = product.alt;
        const link = this.querySelector('.scene-product-link');
        link.textContent = product.name + ' ↗';
        link.href = product.url;
        const count = String(index + 1).padStart(2, '0') + ' / ' + String(this.products.length).padStart(2, '0');
        this.querySelector('.count').textContent = count;
        this.querySelector('.scene-index').textContent = count;
        this.visual.style.opacity = '1';
        if (manual) this.querySelector('.rotation-status').textContent = this.dataset.selected.replace('[number]', String(index + 1)).replace('[count]', String(this.products.length)).replace('[name]', product.name);
        this.busy = false;
        this.schedule();
      }, this.motion.matches ? 0 : 950);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.transitionTimer);
    }
  }
  if (!customElements.get('cp-stage-menu')) customElements.define('cp-stage-menu', StageMenu);
  if (!customElements.get('cp-stage-rotation')) customElements.define('cp-stage-rotation', StageRotation);

  document.addEventListener('click', async event => {
    const link = event.target.closest('[data-cookie-preferences]');
    if (!link) return;
    event.preventDefault();
    const status = link.parentElement.querySelector('.cookie-status');
    try {
      if (typeof window.privacyBanner?.showPreferences !== 'function') throw new Error('Consent interface unavailable');
      await window.privacyBanner.showPreferences();
      if (status) status.hidden = true;
    } catch { if (status) status.hidden = false; }
  });

  if (document.body.dataset.prelaunch === 'true' || document.body.dataset.checkoutApproved !== 'true') {
    document.addEventListener('submit', event => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const path = new URL(form.action, location.href).pathname;
      if (/\/(cart\/add|checkout)(?:\.|\/|$)/.test(path) || (path.endsWith('/cart') && event.submitter?.name === 'checkout')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
  }
  document.addEventListener('invalid', event => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement) || !field.closest('.signup')) return;
    field.setCustomValidity('');
    if (field.validity.valueMissing) field.setCustomValidity(field.dataset.required || '');
    else if (field.validity.typeMismatch) field.setCustomValidity(field.dataset.invalid || '');
  }, true);
  for (const name of ['input', 'change']) document.addEventListener(name, event => {
    if (event.target instanceof HTMLInputElement && event.target.closest('.signup')) event.target.setCustomValidity('');
  });
  const success = document.querySelector('.signup-success');
  if (success && /customer_posted|contact_posted/.test(location.search)) {
    const focusSuccess = () => requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active === document.body || active === document.documentElement || active === success.closest('.newsletter')) {
        success.focus({ preventScroll: false });
      }
    });
    if (document.readyState === 'complete') focusSuccess();
    else window.addEventListener('load', focusSuccess, { once: true });
  }
})();
